import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { searchTutors } from '@/lib/tutor-api';
import type { PublicTutorCard } from '@/lib/schemas/tutor-profile';

interface TutorSearchDropdownProps {
  accessToken: string;
  selectedTutor: PublicTutorCard | null;
  onSelectTutor: (tutor: PublicTutorCard | null) => void;
  open: boolean;
}

const SEARCH_DEBOUNCE_MS = 500;

export function TutorSearchDropdown({
  accessToken,
  selectedTutor,
  onSelectTutor,
  open,
}: TutorSearchDropdownProps) {
  const [tutorSearchInput, setTutorSearchInput] = useState('');
  const [showTutorDropdown, setShowTutorDropdown] = useState(false);
  const [tutorSearchResults, setTutorSearchResults] = useState<PublicTutorCard[]>([]);
  const [isTutorSearching, setIsTutorSearching] = useState(false);

  // Reset local search state if the modal closes
  useEffect(() => {
    if (!open) {
      setTutorSearchInput('');
      setShowTutorDropdown(false);
      setTutorSearchResults([]);
    }
  }, [open]);

  // Fetch tutors based on search input
  useEffect(() => {
    if (!open || !tutorSearchInput.trim() || !accessToken) {
      setTutorSearchResults([]);
      return;
    }

    let cancelled = false;
    setIsTutorSearching(true);

    const timer = setTimeout(() => {
      (async () => {
        try {
          const result = await searchTutors(accessToken, {
            name: tutorSearchInput.trim(),
            pageSize: 10,
          });
          if (!cancelled) {
            setTutorSearchResults(result.results);
          }
        } catch {
          if (!cancelled) {
            setTutorSearchResults([]);
          }
        } finally {
          if (!cancelled) {
            setIsTutorSearching(false);
          }
        }
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tutorSearchInput, open, accessToken]);

  if (selectedTutor) {
    return (
      <div className="mt-1 flex items-center gap-2 h-9">
        <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 h-9 pl-0 pr-3 dark:bg-blue-950/35 overflow-hidden border border-blue-100 dark:border-blue-900/30">
          {selectedTutor.photoUrl ? (
            <img
              src={selectedTutor.photoUrl}
              alt={selectedTutor.displayName}
              className="h-9 w-9 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-200 text-sm font-semibold text-blue-700 dark:bg-blue-900/60 dark:text-blue-300 shrink-0">
              {selectedTutor.displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
            {selectedTutor.displayName}
          </span>
          <button
            type="button"
            onClick={() => {
              onSelectTutor(null);
              setTutorSearchInput('');
              setShowTutorDropdown(false);
            }}
            className="ml-1 rounded-full p-0.5 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/40 cursor-pointer"
            aria-label="Remove tutor"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mt-1">
      <Input
        id="tutor-search"
        type="text"
        placeholder="Search for a tutor..."
        value={tutorSearchInput}
        onChange={(e) => {
          setTutorSearchInput(e.target.value);
          setShowTutorDropdown(true);
        }}
        onFocus={() => {
          if (tutorSearchInput) setShowTutorDropdown(true);
        }}
        className="w-full h-9"
        autoComplete="off"
      />

      {/* Loading indicator */}
      {isTutorSearching && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600"></div>
        </div>
      )}

      {/* Tutor Search Results Dropdown */}
      {showTutorDropdown && tutorSearchInput && (
        <div className="absolute top-full left-0 right-0 z-10 mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-md dark:border-slate-600 dark:bg-slate-800">
          {tutorSearchResults.length > 0 ? (
            tutorSearchResults.map((tutor) => (
              <button
                key={tutor.id}
                type="button"
                onClick={() => {
                  onSelectTutor(tutor);
                  setShowTutorDropdown(false);
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/60 cursor-pointer"
              >
                {tutor.photoUrl ? (
                  <img
                    src={tutor.photoUrl}
                    alt={tutor.displayName}
                    className="h-8 w-8 rounded-full object-cover animate-fade-in"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700 dark:bg-blue-900/60 dark:text-blue-300">
                    {tutor.displayName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                    {tutor.displayName}
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 truncate">
                    {tutor.subjects.join(', ')}
                  </p>
                </div>
                <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex-shrink-0">
                  ${Math.round(tutor.hourlyRate.usdMinor / 100)}/hr
                </span>
              </button>
            ))
          ) : !isTutorSearching ? (
            <div className="px-4 py-6 text-center text-sm text-slate-600 dark:text-slate-400">
              No tutors found matching "{tutorSearchInput}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
